import { useEffect, useMemo, useState } from 'react'
import { userApi } from '../utils/api'
import { formatDateForDisplay } from '../utils/date'

export default function MyContributions() {
  const [contributions, setContributions] = useState([])
  const [selectedBox, setSelectedBox] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadContributions = async (boxId) => {
    try {
      setLoading(true)
      setError(null)
      const activity = await userApi.getContributions(boxId || undefined)
      setContributions(activity)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadContributions(selectedBox)
  }, [selectedBox])

  const boxOptions = useMemo(() => {
    const unique = new Map()
    contributions.forEach((item) => {
      if (!unique.has(item.box_id)) {
        unique.set(item.box_id, item.box_name)
      }
    })
    return Array.from(unique.entries()).map(([id, name]) => ({ id, name }))
  }, [contributions])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500">Loading your activity...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
        {error}
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">My Contributions</h1>
          <p className="mt-2 text-sm text-gray-600">
            Track every contribution and payout you&apos;ve made across all boxes—those you manage and those you joined.
          </p>
        </div>

        <div className="bg-white shadow-xl rounded-3xl p-6 sm:p-8 mb-6 border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Filter by box</p>
              <p className="text-xs text-gray-500">Show contributions from specific boxes you belong to.</p>
            </div>
            <select
              value={selectedBox}
              onChange={(event) => setSelectedBox(event.target.value)}
              className="block w-full sm:w-64 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 shadow-sm"
            >
              <option value="">All boxes</option>
              {boxOptions.map((boxOption) => (
                <option key={boxOption.id} value={boxOption.id}>
                  {boxOption.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-100">
          {contributions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No contributions found {selectedBox ? 'for this box yet.' : 'yet.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Box
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contributions.map((item) => (
                    <tr key={item.entry_id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDateForDisplay(item.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.box_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.role === 'organizer'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {item.role === 'organizer' ? 'Organizer' : 'Member'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            item.type === 'contribution'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {item.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-right text-gray-900">
                        ${Number(item.amount).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.notes || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
